import { Add, Remove } from '@mui/icons-material';
import { Alert, Box, IconButton, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type {
  RuteroTicketQuantity,
  RuteroTourSession,
} from '@bandido/shared/types';

import {
  canAddTourTicketQuantity,
  tourTicketQuantitiesValid,
  tourTicketQuantitiesVisible,
  tourTicketQuantity,
} from '../tourSessions';

interface Props {
  session: RuteroTourSession;
  selection: readonly RuteroTicketQuantity[];
  onChange: (id: string, delta: 1 | -1) => void;
}

/** Cantidades nativas publicadas por la sesión; no se derivan de ADT/CHD/INF. */
export function TourTicketQuantities({ session, selection, onChange }: Props) {
  const { t } = useTranslation();
  if (!tourTicketQuantitiesVisible(session, selection)) return null;

  const options = session.ticketOptions ?? [];
  if (!options.length) {
    return (
      <Alert severity="warning">
        {t('travelServices.tours.ticketQuantitiesUnavailable')}
      </Alert>
    );
  }

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">
        {t('travelServices.tours.ticketQuantities')}
      </Typography>
      {options.map((option) => {
        const quantity = tourTicketQuantity(selection, option.id);
        return (
          <Box
            key={option.id}
            display="flex"
            alignItems="center"
            gap={1.5}
            px={1.5}
            py={1}
            borderRadius={1}
            bgcolor={quantity > 0 ? 'action.selected' : 'grey.100'}
          >
            <Typography variant="body2" flex={1}>
              {option.name}
            </Typography>
            <IconButton
              size="small"
              disabled={quantity === 0}
              onClick={() => onChange(option.id, -1)}
            >
              <Remove fontSize="small" />
            </IconButton>
            <Typography variant="body2" minWidth={16} textAlign="center">
              {quantity}
            </Typography>
            <IconButton
              size="small"
              disabled={
                !canAddTourTicketQuantity(session, selection, option.id)
              }
              onClick={() => onChange(option.id, 1)}
            >
              <Add fontSize="small" />
            </IconButton>
          </Box>
        );
      })}
      {!tourTicketQuantitiesValid(session, selection) && (
        <Typography variant="caption" color="warning.main">
          {t('travelServices.tours.ticketQuantitiesRequired')}
        </Typography>
      )}
    </Stack>
  );
}
